/**
 * BizSuite unified toast system.
 *
 * Options 4 / 5 / 7 from the mockup rolled into one imperative API.
 * Import and call anywhere — no need to pass refs around.
 *
 *   toast.success('Employee saved')
 *   toast.info('Deploy queued')
 *   toast.warn('Session expires in 2 minutes')
 *   toast.error('Failed to save: …')
 *
 *   // With actions (Option 4 — rich notification)
 *   toast.show({
 *     kind: 'info',
 *     title: 'New leave request',
 *     body:  'Nabeel requested 3 days (27–29 Apr)',
 *     actions: [
 *       { label: 'Approve', variant: 'primary', onClick: () => approve(id) },
 *       { label: 'Decline', onClick: () => decline(id) },
 *     ],
 *     sticky: true,     // don't auto-dismiss
 *   })
 *
 *   // Confirm (Option 5 — blocking modal)
 *   const ok = await toast.confirm({
 *     title: 'Delete employee?',
 *     body:  'E001 · Nabeel Patel and all linked records will be removed.',
 *     danger: true,
 *     confirmLabel: 'Delete',
 *   });
 *   if (ok) { … }
 *
 *   // Progress (Option 7 — long-running op)
 *   const p = toast.progress({ title: 'Deploying to production…' });
 *   p.update({ pct: 62, meta: 'Running post-deploy migrations · 3/7' });
 *   p.done({ kind: 'success', title: 'Deployed', body: 'All checks passed.' });
 *   // or p.fail({ title: 'Deploy failed', body: err.message })
 */

const listeners = new Set();
const state = { toasts: [] }; // { id, kind, title?, body?, actions?, sticky?, progress? }

let _seq = 0;
function nextId() { return 't' + (++_seq) + '-' + Date.now().toString(36); }

function notify() {
  for (const l of listeners) l(state.toasts);
}

export function subscribe(listener) {
  listeners.add(listener);
  listener(state.toasts);
  return () => listeners.delete(listener);
}

export function dismiss(id) {
  state.toasts = state.toasts.filter(t => t.id !== id);
  notify();
}

export function dismissAll() {
  state.toasts = [];
  notify();
}

function add(t) {
  const id = nextId();
  const toast = { id, ...t };
  state.toasts = [...state.toasts, toast];
  notify();
  // Auto-dismiss unless sticky or progress
  if (!toast.sticky && !toast.progress && !toast.confirm) {
    const ttl = toast.ttl || (toast.kind === 'error' ? 6000 : 3800);
    setTimeout(() => dismiss(id), ttl);
  }
  return id;
}

/* ─── Simple shortcuts (Option 4 rich card, no actions) ─── */
function mk(kind) {
  return (titleOrOpts, body) => {
    if (typeof titleOrOpts === 'object') return add({ kind, ...titleOrOpts });
    return add({ kind, title: titleOrOpts, body });
  };
}

/* ─── Option 4 rich card, full control ─── */
function show(opts) {
  return add({ kind: 'info', ...opts });
}

/* ─── Option 5: confirm modal ─── */
function confirmDialog(opts) {
  return new Promise(resolve => {
    const id = nextId();
    const t = {
      id,
      confirm: true,
      title: opts.title || 'Are you sure?',
      body: opts.body || '',
      danger: !!opts.danger,
      confirmLabel: opts.confirmLabel || (opts.danger ? 'Delete' : 'Confirm'),
      cancelLabel: opts.cancelLabel || 'Cancel',
      onResolve: (result) => {
        dismiss(id);
        resolve(result);
      },
    };
    state.toasts = [...state.toasts, t];
    notify();
  });
}

/* ─── Option 7: progress toast with handle ─── */
function progress(opts = {}) {
  const id = add({
    kind: 'info',
    sticky: true,
    progress: { pct: opts.pct ?? 0, meta: opts.meta || '' },
    title: opts.title || 'Working…',
    body:  opts.body  || '',
  });
  return {
    id,
    update(patch = {}) {
      const t = state.toasts.find(x => x.id === id);
      if (!t) return;
      if (patch.title !== undefined) t.title = patch.title;
      if (patch.body  !== undefined) t.body  = patch.body;
      if (patch.pct   !== undefined) t.progress.pct = patch.pct;
      if (patch.meta  !== undefined) t.progress.meta = patch.meta;
      state.toasts = [...state.toasts];
      notify();
    },
    done(opts2 = {}) {
      dismiss(id);
      add({ kind: opts2.kind || 'success', title: opts2.title || 'Done', body: opts2.body || '' });
    },
    fail(opts2 = {}) {
      dismiss(id);
      add({ kind: 'error', title: opts2.title || 'Failed', body: opts2.body || '' });
    },
    dismiss() { dismiss(id); },
  };
}

export const toast = {
  success: mk('success'),
  info:    mk('info'),
  warn:    mk('warn'),
  error:   mk('error'),
  show,
  confirm: confirmDialog,
  progress,
  dismiss,
  dismissAll,
};

export default toast;
